import { useMemo, useRef } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

// Spinning Globe Component

function SpinningGlobe() {
  const globeRef = useRef<THREE.Mesh | null>(null);

  const texture = useLoader(THREE.TextureLoader, '/assets/textures/earthmap2.jpg');

  // Rotate the globe continuously using useFrame
  useFrame(() => {
    if (globeRef.current) {
      globeRef.current.rotation.y += 0.005; // Change rotation speed here
    }
  });

  return (
    <mesh ref={globeRef} rotation={[0, 0, 0]}>
      {/* Sphere for the globe */}
      <sphereGeometry args={[4, 32, 32]} />
      <meshStandardMaterial map={texture} />
    </mesh>
  );
}

// Stars Component
interface StarsProps {
  count?: number;
}

function Stars({ count = 5000 }: StarsProps): JSX.Element {
  const starPositions = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const distance = 2;
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * distance * 100;
      positions[i * 3 + 1] = (Math.random() - 0.5) * distance * 100;
      positions[i * 3 + 2] = (Math.random() - 0.5) * distance * 100;
    }
    return positions;
  }, [count]);

  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" array={starPositions} count={count} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial color="white" size={0.4} />
    </points>
  );
}

export function GlobeScene(): JSX.Element {
  return (
    <Canvas style={{ width: '100%', height: '100%', borderRadius: '10px' }} camera={{ position: [0, 0, 10] }} gl={{ alpha: true }}>
      <color attach="background" args={['#000011']} />
      {/* Lighting */}
      <ambientLight intensity={0.7} />
      <directionalLight
        position={[5, 5, 5]} // Simulating the sun's position
        intensity={5} // Sunlight intensity
        castShadow // Optional: Enable shadows from the globe
      />

      <Stars count={1000} />
      <SpinningGlobe />
      <OrbitControls enableZoom={false} />
    </Canvas>
  );
}
